/**
 * Text-to-speech integration (ElevenLabs, Edge TTS, Web Speech).
 *
 * Flow:
 * 1. Fetch TTS config from gateway (talk.config with includeSecrets)
 * 2. Call the selected TTS provider:
 *    - ElevenLabs: direct browser → API
 *    - Edge TTS: POST to beta backend /api/tts
 *    - Web Speech: browser SpeechSynthesis API (fallback)
 * 3. Decode and play through AudioContext
 *
 * Features:
 * - Sentence splitting for streaming playback
 * - Audio caching for short phrases (≤40 chars, max 100 entries)
 * - Web Speech API fallback when other providers are unavailable
 * - Emoji/markdown stripping before TTS
 * - In-flight request deduplication
 * - Configurable TTS provider selection
 */

import type { WSClient } from '../ws/client';
import type { TalkConfigParams, TalkConfigPayload } from '../types/protocol';
import type { TtsConfig, TtsProvider, EdgeTtsConfig } from '../types/gateway';
import { STORAGE_KEYS } from '../utils/constants';
import { readString } from '../utils/storage';
import { getApiUrl } from '../utils/environment';
import { getApiAuthHeaders, hasAnyAuth } from '../utils/auth';
import { getEdgeVoiceForText, VoiceLockManager } from './langDetect';
import { remoteLog } from '../utils/remoteLog';

// ── Constants ──

const TTS_CACHE_MAX_CHARS = 40;
const TTS_CACHE_MAX_SIZE = 100;

export type TtsVisibleErrorCode =
  | 'edge-missing-auth'
  | 'edge-auth-rejected'
  | 'edge-http-error'
  | 'edge-network-error';

export class TtsVisibleError extends Error {
  readonly provider: TtsProvider;
  readonly code: TtsVisibleErrorCode;
  readonly status?: number;
  readonly userMessage: string;

  constructor(params: {
    provider: TtsProvider;
    code: TtsVisibleErrorCode;
    message: string;
    userMessage: string;
    status?: number;
    cause?: unknown;
  }) {
    super(params.message, { cause: params.cause });
    this.name = 'TtsVisibleError';
    this.provider = params.provider;
    this.code = params.code;
    this.status = params.status;
    this.userMessage = params.userMessage;
  }
}

// ── Config Fetching ──

/**
 * Fetch TTS configuration from the gateway.
 *
 * Contains voice settings and API key for ElevenLabs.
 * Should be called after successful connect and cached in memory
 * (not localStorage — contains API key).
 */
export async function fetchTtsConfig(
  client: WSClient,
): Promise<TtsConfig | null> {
  try {
    const payload = await client.request<TalkConfigParams, TalkConfigPayload>(
      'talk.config',
      { includeSecrets: true },
    );

    // Gateway wraps fields in config.talk; fall back to flat payload for compat
    const talk = payload.config?.talk ?? payload;

    return {
      voiceId: talk.voiceId,
      modelId: talk.modelId,
      outputFormat: talk.outputFormat,
      apiKey: talk.apiKey ?? '',
      interruptOnSpeech: talk.interruptOnSpeech ?? true,
    };
  } catch (err) {
    console.warn('[tts] Failed to fetch talk.config:', err);
    return null;
  }
}

// ── Text Processing ──

/**
 * Strip markdown formatting and emojis for cleaner TTS output.
 * Keeps the readable text but removes visual-only elements.
 */
export function stripMarkdownForTTS(text: string): string {
  return (
    text
      .replace(/\*+/g, '') // asterisks (bold/italic)
      .replace(/_+/g, '') // underscores (bold/italic)
      .replace(/~~/g, '') // strikethrough
      .replace(/`+/g, '') // code blocks
      .replace(/^#{1,6}\s/gm, '') // headings
      .replace(/^\s*[-*+]\s/gm, '') // list markers
      .replace(/^\s*>\s/gm, '') // blockquotes
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links — keep text, remove URL
      .replace(/^---+$/gm, '') // horizontal rules
      // Strip bare URLs entirely (markdown links already handled above)
      .replace(/https?:\/\/([^/\s]+)\S*/g, '')
      // Emojis
      .replace(/\p{Extended_Pictographic}/gu, '')
      .replace(/[\u{200D}\u{FE0F}]/gu, '') // leftover joiners/selectors
      .trim()
  );
}

/**
 * Split text into sentence-sized chunks for streaming TTS.
 *
 * Splits on sentence boundaries (period/exclamation/question followed
 * by whitespace, or newlines). Preserves the splitting punctuation.
 */
export function splitIntoSentences(text: string): string[] {
  const sentences: string[] = [];
  let buffer = text;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const match = buffer.match(/^(.*?(?:[.!?]\s|\n))/s);
    if (!match) break;

    const sentence = match[1]!;
    const trimmed = sentence.trim();
    if (trimmed) {
      sentences.push(sentence);
    }
    buffer = buffer.slice(sentence.length);
  }

  // Remaining text (no trailing sentence boundary)
  const remaining = buffer.trim();
  if (remaining) {
    sentences.push(buffer);
  }

  return sentences;
}

// ── Edge TTS Helpers ──

/**
 * Read Edge TTS configuration from localStorage.
 */
export function getEdgeTtsConfig(): EdgeTtsConfig {
  return {
    backendUrl: readString(STORAGE_KEYS.EDGE_TTS_BACKEND_URL, getApiUrl()),
    authToken: readString(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN, ''),
  };
}

/**
 * Read the currently selected TTS provider from localStorage.
 * Defaults to 'elevenlabs' for backward compatibility.
 */
export function getTtsProvider(): TtsProvider {
  const stored = readString(STORAGE_KEYS.TTS_PROVIDER, 'elevenlabs');
  if (stored === 'edge' || stored === 'elevenlabs' || stored === 'webspeech') {
    return stored;
  }
  return 'elevenlabs';
}

// ── TTS Manager ──

/**
 * Manages TTS synthesis with caching, deduplication, and fallback.
 *
 * Supports three providers:
 * - ElevenLabs: direct browser → ElevenLabs API (needs API key from gateway)
 * - Edge TTS: POST to beta backend /api/tts (needs backend URL + auth token)
 * - Web Speech: browser SpeechSynthesis API (handled at hook layer)
 */
export class TtsManager {
  private config: TtsConfig | null = null;
  private cache = new Map<string, ArrayBuffer>();
  private inflight = new Map<string, Promise<ArrayBuffer | null>>();
  private voiceLock = new VoiceLockManager();
  pendingTexts: string[] = [];

  /** Set the TTS config (from fetchTtsConfig) */
  setConfig(config: TtsConfig | null): void {
    this.config = config;
  }

  /** Get current config */
  getConfig(): TtsConfig | null {
    return this.config;
  }

  /** Whether ElevenLabs is available (has API key) */
  get isElevenLabsAvailable(): boolean {
    return !!this.config?.apiKey;
  }

  /** Whether Edge TTS is available (has auth — JWT or legacy token) */
  get isEdgeTtsAvailable(): boolean {
    const cfg = getEdgeTtsConfig();
    return !!cfg.backendUrl && (!!cfg.authToken || hasAnyAuth(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN));
  }

  /** Get the currently selected TTS provider */
  get provider(): TtsProvider {
    return getTtsProvider();
  }

  /**
   * Synthesize text to an ArrayBuffer of audio data.
   *
   * Routes to the selected provider (Edge TTS, ElevenLabs, or returns null
   * for Web Speech which is handled at the hook/playback layer).
   * Uses caching for short phrases and deduplication for in-flight requests.
   *
   * @returns ArrayBuffer of audio data, or null on failure
   */
  async synthesize(text: string): Promise<ArrayBuffer | null> {
    const cleanText = stripMarkdownForTTS(text).trim();
    if (!cleanText) return null;

    // Web Speech is handled at the hook layer, not here
    if (this.provider === 'webspeech') return null;

    // Check cache first
    const cached = this.cacheGet(cleanText);
    if (cached) return cached;

    // Deduplicate in-flight requests
    const cacheKey = this.makeCacheKey(cleanText);
    const existing = this.inflight.get(cacheKey);
    if (existing) return existing;

    const promise = this.doSynthesize(cleanText);
    if (cleanText.length <= TTS_CACHE_MAX_CHARS) {
      this.inflight.set(cacheKey, promise);
      promise.finally(() => this.inflight.delete(cacheKey));
    }

    return promise;
  }

  /** Reset the voice lock (call on new response stream) */
  resetVoiceLock(runId?: string): void {
    this.voiceLock.reset(runId);
    this.pendingTexts = [];
  }

  /** Clear the audio cache */
  clearCache(): void {
    this.cache.clear();
  }

  private async doSynthesize(text: string): Promise<ArrayBuffer | null> {
    const currentProvider = this.provider;

    // Route to the selected provider
    if (currentProvider === 'edge') {
      const voice = this.voiceLock.getVoice(text);
      if (voice === null) {
        // Still buffering — hold this text until we have enough words to detect
        this.pendingTexts.push(text);
        return null;
      }

      let synthesizeText = text;
      if (this.pendingTexts.length > 0) {
        synthesizeText = [...this.pendingTexts, text].join(' ');
        this.pendingTexts = [];
      }

      const result = await this.synthesizeEdgeTTS(synthesizeText, undefined, voice);
      if (result) {
        this.cachePut(synthesizeText, result);
        return result;
      }
      // If Edge TTS fails, don't fall through — return null
      // (hook layer handles Web Speech fallback)
      return null;
    }

    // ElevenLabs (default)
    if (this.config?.apiKey) {
      const result = await this.synthesizeElevenLabs(text);
      if (result) {
        this.cachePut(text, result);
        return result;
      }
    }

    // No fallback to Web Speech API synthesis here — that's handled
    // at the playback level (AudioQueue can't use ArrayBuffer for speech synthesis).
    // The hook layer handles the fallback.
    return null;
  }

  /**
   * Synthesize text using Edge TTS via the beta backend.
   *
   * POST /api/tts with { text, speed } and Bearer auth.
   * Returns audio/mpeg ArrayBuffer.
   */
  private async synthesizeEdgeTTS(
    text: string,
    speed?: number,
    voice?: string,
  ): Promise<ArrayBuffer | null> {
    const edgeConfig = getEdgeTtsConfig();
    if (!edgeConfig.backendUrl || (!edgeConfig.authToken && !hasAnyAuth(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN))) {
      const error = new TtsVisibleError({
        provider: 'edge',
        code: 'edge-missing-auth',
        message: 'Edge TTS not configured (missing auth or URL)',
        userMessage: 'Edge TTS is not configured. Sign in again or reconnect backend audio.',
      });
      console.warn('[tts] Edge TTS not configured (missing auth or URL)');
      remoteLog('tts', 'edge_auth_missing', { backendUrl: !!edgeConfig.backendUrl });
      throw error;
    }

    try {
      const url = `${edgeConfig.backendUrl.replace(/\/+$/, '')}/api/tts`;

      // Use provided voice or auto-detect from text
      const resolvedVoice = voice ?? getEdgeVoiceForText(text);
      remoteLog('tts', 'lang_detected', { voice: resolvedVoice, chunk: text.substring(0, 40) });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiAuthHeaders(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          speed: speed ?? 1.0,
          voice: resolvedVoice,
        }),
      });

      if (!response.ok) {
        const details = await response.text().catch(() => '');
        const authFailure = response.status === 401 || response.status === 403;
        console.warn(
          '[tts] Edge TTS API error:',
          response.status,
          response.statusText,
        );
        remoteLog('tts', 'edge_api_error', {
          status: response.status,
          statusText: response.statusText,
          authFailure,
        });
        throw new TtsVisibleError({
          provider: 'edge',
          code: authFailure ? 'edge-auth-rejected' : 'edge-http-error',
          status: response.status,
          message: `Edge TTS API error ${response.status}: ${response.statusText}${details ? `: ${details}` : ''}`,
          userMessage: authFailure
            ? 'Edge TTS auth expired. Sign in again or reconnect backend audio.'
            : `Edge TTS failed (${response.status} ${response.statusText || 'HTTP error'}).`,
        });
      }

      return await response.arrayBuffer();
    } catch (err) {
      if (err instanceof TtsVisibleError) {
        throw err;
      }
      console.warn('[tts] Edge TTS request failed:', err);
      remoteLog('tts', 'edge_request_failed', { error: String(err) });
      throw new TtsVisibleError({
        provider: 'edge',
        code: 'edge-network-error',
        message: `Edge TTS request failed: ${String(err)}`,
        userMessage: 'Could not reach the Edge TTS backend. Check the backend URL or network.',
        cause: err,
      });
    }
  }

  private async synthesizeElevenLabs(
    text: string,
  ): Promise<ArrayBuffer | null> {
    if (!this.config?.apiKey) return null;

    try {
      const outputFormat = this.config.outputFormat || 'mp3_44100_128';
      const url = new URL(
        `https://api.elevenlabs.io/v1/text-to-speech/${this.config.voiceId}/stream`,
      );
      url.searchParams.set('output_format', outputFormat);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'xi-api-key': this.config.apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          model_id: this.config.modelId,
        }),
      });

      if (!response.ok) {
        console.warn(
          '[tts] ElevenLabs API error:',
          response.status,
          response.statusText,
        );
        return null;
      }

      return await response.arrayBuffer();
    } catch (err) {
      console.warn('[tts] Synthesis request failed:', err);
      return null;
    }
  }

  // ── Cache ──

  private makeCacheKey(text: string): string {
    const voiceId = this.config?.voiceId ?? '';
    const modelId = this.config?.modelId ?? '';
    return `${voiceId}|${modelId}|${text.toLowerCase().trim()}`;
  }

  private cacheGet(text: string): ArrayBuffer | null {
    if (text.length > TTS_CACHE_MAX_CHARS) return null;
    return this.cache.get(this.makeCacheKey(text)) ?? null;
  }

  private cachePut(text: string, buffer: ArrayBuffer): void {
    if (text.length > TTS_CACHE_MAX_CHARS) return;

    const key = this.makeCacheKey(text);
    if (this.cache.has(key)) return;

    // Evict oldest if at capacity
    if (this.cache.size >= TTS_CACHE_MAX_SIZE) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, buffer);
  }
}

// ── Web Speech API Fallback ──

/**
 * Speak text using the browser's Web Speech API.
 *
 * Used as a fallback when ElevenLabs is not available.
 * Returns a promise that resolves when speech is done.
 */
export function speakWithWebSpeech(
  text: string,
  rate: number = 1,
): { cancel: () => void; finished: Promise<void> } {
  const cleanText = stripMarkdownForTTS(text).trim();
  const utterance = new SpeechSynthesisUtterance(cleanText);
  utterance.rate = rate;

  const finished = new Promise<void>((resolve) => {
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
  });

  speechSynthesis.speak(utterance);

  return {
    cancel: () => {
      speechSynthesis.cancel();
    },
    finished,
  };
}

// Re-export for convenience
export type { TtsConfig, TtsProvider, EdgeTtsConfig };
